import { useState } from "react";

import { injectIntl, intlFormatPropTypes } from "react-intl";

import { PaginationControls } from "@ps-design-system/components/Pagination";
import { Table, TableBody } from "@ps-design-system/components/Table";

import { DateRateDeleteDialog } from "./DateRateDeleteDialog";
import { type DateRate } from "./DateRateTable.types";
import { formatDateRateRange } from "./DateRateTable.utils";
import { DateRateTableHeader } from "./DateRateTableHeader";
import { DateRateTableRow } from "./DateRateTableRow";
import { useDateRatePagination } from "./useDateRatePagination";

type DateRateTableProps = {
  currency: string;
  dateRates: DateRate[];
  intl: intlFormatPropTypes;
  onDelete: (dates: string[]) => void;
};

/** Table of the listing's date based rates, with a confirmed delete per rate. */
const DateRateTable = ({
  currency,
  dateRates,
  intl,
  onDelete,
}: DateRateTableProps) => {
  const [datesToDelete, setDatesToDelete] = useState<string[]>();
  const { page, pageCount, pageDateRates, setPage } =
    useDateRatePagination(dateRates);

  const formatHourlyRate = (hourlyRate: number) =>
    currency
      ? intl.formatNumber(hourlyRate, {
          style: "currency",
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      : String(hourlyRate);

  const handleKeepRate = () => setDatesToDelete(undefined);
  const handleDeleteRate = () => {
    if (datesToDelete) {
      onDelete(datesToDelete);
    }
    setDatesToDelete(undefined);
  };

  return (
    <>
      <div
        className="tw-border tw-border-solid tw-border-gray-400 tw-rounded-4 tw-overflow-hidden"
        data-testid="date-rate-table"
      >
        <Table>
          <DateRateTableHeader />
          <TableBody>
            {pageDateRates.map((dateRate) => (
              <DateRateTableRow
                date={formatDateRateRange(dateRate, intl.locale)}
                hourlyRate={formatHourlyRate(dateRate.hourlyRate)}
                key={dateRate.id}
                onDeleteClick={() => setDatesToDelete(dateRate.dates)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {pageCount > 1 && (
        <PaginationControls
          className="tw-mt-16"
          currentPage={page}
          onPageChange={setPage}
          totalPages={pageCount}
        />
      )}
      <DateRateDeleteDialog
        onDelete={handleDeleteRate}
        onKeep={handleKeepRate}
        open={!!datesToDelete}
      />
    </>
  );
};

const DateRateTableWithIntl = injectIntl(DateRateTable);

export { DateRateTableWithIntl as DateRateTable };
